import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Clock, Flower, FlaskConical, Moon } from 'lucide-react';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { BulkPricingSection } from '../components/BulkPricingSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div className="fade-in bg-brand-cream" data-testid="home-page">
      <section
        className="min-h-screen lily-hero-bg flex items-center justify-center px-6 pt-24 md:pt-0 relative"
        data-testid="hero-section"
      >
        <div className="absolute inset-0 bg-gradient-to-b from-brand-cream/30 via-transparent to-brand-cream/60 pointer-events-none" />
        <div className="max-w-4xl text-center relative z-10">
          <div className="inline-block bg-brand-cream/90 backdrop-blur-sm px-8 py-2 mb-6 rounded-sm">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold">
              Premium A-Grade Oriental Lilies
            </p>
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl md:text-7xl font-light tracking-tight leading-none text-brand-green-dark mb-6 drop-shadow-sm">
            Farm-Fresh Elegance,
            <br />
            <span className="italic text-brand-green">Delivered to Your Door</span>
          </h1>
          <div className="bg-brand-cream/85 backdrop-blur-sm p-6 rounded-sm max-w-2xl mx-auto mb-8">
            <p className="font-body text-base sm:text-lg leading-relaxed font-light text-brand-green-dark">
              Handpicked oriental lilies with beautiful aroma, perfectly cut and ready for a beautiful vase or evening.
              Delivered within 2 business days for orders placed before 2 PM · Hyderabad.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <div className="flex items-center gap-2 bg-brand-cream/90 backdrop-blur-sm px-4 py-2 rounded-sm">
              <Sparkles className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-green-dark">A-Grade Quality</span>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream/90 backdrop-blur-sm px-4 py-2 rounded-sm">
              <Clock className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-green-dark">2-Day Delivery</span>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream/90 backdrop-blur-sm px-4 py-2 rounded-sm">
              <Flower className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-green-dark">Vase Ready</span>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream/90 backdrop-blur-sm px-4 py-2 rounded-sm">
              <FlaskConical className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-green-dark">Beautiful Aroma</span>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream/90 backdrop-blur-sm px-4 py-2 rounded-sm">
              <Moon className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-green-dark">Evening Ready</span>
            </div>
          </div>

          <Link
            to="/products"
            className="inline-block bg-brand-green text-brand-cream px-12 py-4 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition shadow-lg"
            data-testid="shop-now-btn"
          >
            Shop Now
          </Link>
        </div>
      </section>

      <section className="py-24 px-6 bg-brand-cream" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
              Our Collection
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-brand-green-dark mb-4">
              Four Signature Lilies
            </h2>
            <p className="font-body text-base text-brand-green-dark/70 max-w-2xl mx-auto">
              A curated selection of A-Grade oriental lilies — each stem perfectly cut,
              beautifully fragrant, and ready for a beautiful evening.
            </p>
          </div>

          {loading ? (
            <div className="text-center text-brand-green-dark">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              {products.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.id}`}
                  className="group"
                  data-testid={`featured-product-${product.id}`}
                >
                  <div className="aspect-[3/4] overflow-hidden mb-4 gold-border-glow bg-brand-blush/20">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover product-image-hover"
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold">
                      A-Grade
                    </span>
                    <span className="text-brand-gold/50">•</span>
                    <span className="text-brand-green-dark/60 text-xs font-body">
                      {product.color}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-light text-brand-green-dark group-hover:text-brand-gold smooth-transition mb-1">
                    {product.name}
                  </h3>
                  <p className="font-body text-xs text-brand-green-dark/60 italic mb-3">
                    {product.variety}
                  </p>
                  <p className="font-body text-brand-green-dark text-lg mb-2">
                    ₹{product.pricePerStem}<span className="text-sm text-brand-green-dark/60"> / stem</span>
                  </p>
                  <p className="font-body text-xs text-brand-gold">
                    Ready for a beautiful vase or evening
                  </p>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-block border border-brand-green text-brand-green-dark px-10 py-3 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-green hover:text-brand-cream smooth-transition"
              data-testid="view-all-btn"
            >
              View All
            </Link>
          </div>
        </div>
      </section>

      <BulkPricingSection />

      <TestimonialsSection />

      <section className="py-24 px-6 lily-story-bg" data-testid="brand-story-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-6">
            Our Story
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-brand-cream mb-8">
            Cultivated with Care,
            <br />
            <span className="italic">Delivered with Love</span>
          </h2>
          <p className="font-body text-base sm:text-lg leading-relaxed font-light text-brand-cream/95 mb-6">
            FarmFreshLily brings you the finest A-grade oriental lilies,
            grown with dedication on our local farms. Each stem is handpicked at peak bloom,
            expertly cut, and delivered fresh to your door within 2 business days.
          </p>
          <p className="font-body text-base sm:text-lg leading-relaxed font-light text-brand-cream/95 mb-8">
            Experience the beautiful aroma and elegance of premium lilies,
            perfectly ready for your vase or evening gathering. Serving Hyderabad with luxury, freshness, and care.
          </p>
          <Link
            to="/about"
            className="inline-block border border-brand-gold text-brand-gold px-10 py-3 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
            data-testid="learn-more-btn"
          >
            Learn More
          </Link>
        </div>
      </section>
    </div>
  );
};
